import { Link } from '@tanstack/react-router';
import { formatInTimeZone } from 'date-fns-tz';
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import {
    OUTREACH_ANALYTICS_TIMEZONE,
    type OutreachUrgencyFilter,
} from '@apps/web/components/admin/outreach_observability/constants';
import {
    formatCompact,
    formatDurationMs,
    formatRate,
    formatTokens,
} from '@apps/web/components/admin/outreach_observability/tool_stats_table';
import { Badge } from '@apps/web/components/ui/badge';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@apps/web/components/ui/sheet';
import { useAdminOutreachToolRuns } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type ToolRunListItem,
    type ToolStatRow,
} from '@libraries/typescript/api_client';

type ToolDetailDrawerProps = {
    // Null closes the sheet; non-null opens it on that tool's detail.
    readonly tool: ToolStatRow | null;
    readonly organizationIds: Array<string>;
    readonly urgency: OutreachUrgencyFilter;
    readonly onClose: () => void;
};

const HOURLY_SERIES: Array<{
    readonly key: 'callCount' | 'failedCount';
    readonly label: string;
    readonly color: string;
}> = [
    { key: 'callCount', label: 'Calls', color: 'var(--chart-1)' },
    { key: 'failedCount', label: 'Failures', color: 'var(--destructive)' },
];

function formatHourLabel(iso: string): string {
    return formatInTimeZone(
        new Date(iso),
        OUTREACH_ANALYTICS_TIMEZONE,
        'M/d ha',
    );
}

function formatCallTime(iso: string): string {
    return formatInTimeZone(
        new Date(iso),
        OUTREACH_ANALYTICS_TIMEZONE,
        'M/d h:mm a',
    );
}

function StatChip({
    label,
    value,
}: {
    readonly label: string;
    readonly value: string;
}) {
    return (
        <div className="flex flex-col rounded-md border px-3 py-2">
            <span className="text-muted-foreground text-xs tracking-wide uppercase">
                {label}
            </span>
            <span className="text-sm font-semibold tabular-nums">{value}</span>
        </div>
    );
}

function HourlyTooltip({
    active,
    payload,
    label,
}: {
    readonly active?: boolean;
    readonly payload?: Array<{
        readonly name?: string;
        readonly value?: number;
        readonly color?: string;
    }>;
    readonly label?: string;
}) {
    if (!active || !payload || payload.length === 0) {
        return null;
    }
    return (
        <div className="bg-background rounded-md border px-3 py-2 text-xs shadow-sm">
            <div className="mb-1 font-medium">{label}</div>
            {payload.map((entry) => (
                <div
                    key={entry.name}
                    className="flex items-center justify-between gap-4"
                >
                    <span style={{ color: entry.color }}>{entry.name}</span>
                    <span className="tabular-nums">{entry.value ?? 0}</span>
                </div>
            ))}
        </div>
    );
}

function HourlyTrendChart({ tool }: { readonly tool: ToolStatRow }) {
    const data = tool.hourly.map((bucket) => ({
        label: formatHourLabel(bucket.bucket_start),
        callCount: bucket.call_count,
        failedCount: bucket.failed_count,
    }));
    if (data.length === 0) {
        return (
            <div className="text-muted-foreground flex h-56 items-center justify-center rounded-md border text-sm">
                No hourly calls to plot.
            </div>
        );
    }
    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-3 text-xs">
                {HOURLY_SERIES.map((series) => (
                    <span
                        key={series.key}
                        className="flex items-center gap-1.5"
                    >
                        <span
                            className="inline-block h-0 w-4 border-t-2"
                            style={{ borderColor: series.color }}
                        />
                        <span className="text-muted-foreground">
                            {series.label}
                        </span>
                    </span>
                ))}
            </div>
            <div className="h-56 w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data} margin={{ left: 8, right: 8 }}>
                        <CartesianGrid
                            stroke="var(--border)"
                            vertical={false}
                        />
                        <XAxis
                            dataKey="label"
                            tick={{ fontSize: 11 }}
                            minTickGap={24}
                        />
                        <YAxis
                            tick={{ fontSize: 11 }}
                            width={32}
                            allowDecimals={false}
                        />
                        <RechartsTooltip content={<HourlyTooltip />} />
                        {HOURLY_SERIES.map((series) => (
                            <Line
                                key={series.key}
                                type="monotone"
                                dataKey={series.key}
                                name={series.label}
                                stroke={series.color}
                                dot={false}
                                strokeWidth={2}
                                isAnimationActive={false}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function WorstCallsTable({
    runs,
    isLoading,
    isError,
}: {
    readonly runs: Array<ToolRunListItem>;
    readonly isLoading: boolean;
    readonly isError: boolean;
}) {
    if (runs.length === 0) {
        if (isLoading) {
            return (
                <div className="text-muted-foreground py-6 text-center text-sm">
                    Loading calls…
                </div>
            );
        }
        if (isError) {
            return (
                <div className="text-destructive py-6 text-center text-sm">
                    Failed to load calls.
                </div>
            );
        }
        return (
            <div className="text-muted-foreground py-6 text-center text-sm">
                No calls in this window.
            </div>
        );
    }
    return (
        <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-xs whitespace-nowrap">
                <thead className="bg-muted/50 sticky top-0">
                    <tr className="text-muted-foreground text-left">
                        <th className="px-3 py-2 font-medium">Time</th>
                        <th className="px-3 py-2 font-medium">Org</th>
                        <th className="px-3 py-2 text-right font-medium">
                            Duration
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                            Queue wait
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                            Args
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                            Attempts
                        </th>
                        <th className="px-3 py-2 font-medium">Status</th>
                        <th className="px-3 py-2 font-medium">Trace</th>
                    </tr>
                </thead>
                <tbody>
                    {runs.map((run) => (
                        <tr key={run.run_id} className="border-t">
                            <td className="px-3 py-2 tabular-nums">
                                {formatCallTime(run.created_at)}
                            </td>
                            <td className="px-3 py-2">{run.org_name}</td>
                            <td className="px-3 py-2 text-right tabular-nums">
                                {formatDurationMs(run.duration_ms)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                                {formatDurationMs(run.queue_wait_ms)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                                {formatCompact(run.args_chars)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                                {run.attempts}
                            </td>
                            <td className="px-3 py-2">
                                <Badge
                                    variant={
                                        run.status === 'succeeded'
                                            ? 'secondary'
                                            : 'destructive'
                                    }
                                >
                                    {run.status}
                                </Badge>
                            </td>
                            <td className="px-3 py-2">
                                {run.agent_conversation_id ? (
                                    <Link
                                        to="/admin/agent-traces/$conversationId"
                                        params={{
                                            conversationId:
                                                run.agent_conversation_id,
                                        }}
                                        className="text-primary hover:underline"
                                    >
                                        Trace
                                    </Link>
                                ) : null}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function ToolDetailDrawer({
    tool,
    organizationIds,
    urgency,
    onClose,
}: ToolDetailDrawerProps) {
    const open = tool !== null;
    // The drill-down only fetches while the sheet is open on a tool.
    const runsQuery = useAdminOutreachToolRuns(
        tool?.tool_name ?? null,
        organizationIds,
        open,
        urgency,
    );
    const runsData = runsQuery.data;
    const errorRate =
        tool && tool.call_count > 0
            ? tool.failed_count / tool.call_count
            : null;
    return (
        <Sheet
            open={open}
            onOpenChange={(next) => {
                if (!next) {
                    onClose();
                }
            }}
        >
            <SheetContent className="w-full gap-0 overflow-y-auto sm:max-w-2xl">
                {tool ? (
                    <div className="flex flex-col gap-4 p-4">
                        <SheetHeader className="p-0">
                            <SheetTitle>{tool.tool_name}</SheetTitle>
                            <SheetDescription>
                                {tool.call_count} calls ·{' '}
                                {formatRate(errorRate)} error rate in this
                                window.
                            </SheetDescription>
                        </SheetHeader>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                            <StatChip
                                label="Calls"
                                value={String(tool.call_count)}
                            />
                            <StatChip
                                label="Error rate"
                                value={formatRate(errorRate)}
                            />
                            <StatChip
                                label="Retries"
                                value={String(tool.retry_count)}
                            />
                            <StatChip
                                label="p50 / p95"
                                value={`${formatDurationMs(
                                    tool.p50_duration_ms,
                                )} / ${formatDurationMs(tool.p95_duration_ms)}`}
                            />
                            <StatChip
                                label="p95 queue wait"
                                value={formatDurationMs(tool.p95_queue_wait_ms)}
                            />
                            <StatChip
                                label="Args avg / max"
                                value={`${formatCompact(
                                    tool.avg_args_chars,
                                )} / ${formatCompact(tool.max_args_chars)}`}
                            />
                            <StatChip
                                label="Tokens avg / p95"
                                value={`${formatTokens(
                                    tool.avg_turn_output_tokens,
                                )} / ${formatTokens(
                                    tool.p95_turn_output_tokens,
                                )}`}
                            />
                        </div>
                        <HourlyTrendChart tool={tool} />
                        <div className="flex flex-col gap-2">
                            <h3 className="text-sm font-semibold">
                                Slowest calls
                            </h3>
                            <WorstCallsTable
                                runs={runsData?.runs ?? []}
                                isLoading={runsQuery.isLoading}
                                isError={
                                    runsQuery.error !== undefined ||
                                    (runsQuery.hasRequestFailed &&
                                        runsData === undefined)
                                }
                            />
                            {runsData?.truncated ? (
                                <p className="text-muted-foreground text-center text-xs">
                                    Showing {runsData.runs.length} of{' '}
                                    {runsData.total_matching} calls (slowest
                                    first).
                                </p>
                            ) : null}
                        </div>
                    </div>
                ) : null}
            </SheetContent>
        </Sheet>
    );
}
